import React, { useMemo } from 'react';

import './style.css';

import {
  Bar,
  BarChart,
  Brush,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
  type TooltipPayload,
} from 'recharts';

import { RECHARTS_COLORS } from '../../../constants/charts.js';
import {
  CHART_COLORS_IN_ORDER_TAILWIND,
  colorToHex,
  getChartFullWidth,
} from '../../../helpers/charts.js';
import type { ChartProps } from '../../../types/charts.js';
import BarChartLegend from './BarChartLegend.jsx';
import BarChartTooltip from './BarChartTooltip.jsx';
import useBarChart from './useBarChart.jsx';

const MAX_CHART_DISPLAY = 8;
const MIN_BRUSH_WIDTH = 0.2;

const CustomBarChart = ({
  x,
  y,
  dataframe,
  isMobile = false,
  isAgentic = false,
  isExport,
}: ChartProps) => {
  const colors = CHART_COLORS_IN_ORDER_TAILWIND;

  const isCompactView = isMobile || isAgentic;

  const { xAxisProps, yAxisProps, barChartHeight } = useBarChart({
    x,
    length: dataframe.length,
    isCompactView: isCompactView,
  });

  const renderTooltip = (
    payload: TooltipPayload | undefined,
    active: boolean,
  ) => <BarChartTooltip contentData={payload} isActive={active} xValue={x} />;

  const shouldRenderBrush = dataframe.length > MAX_CHART_DISPLAY;

  const calculateBrushRange = () => {
    if (!isCompactView) {
      return { startIndex: 0, endIndex: MAX_CHART_DISPLAY };
    }

    const maxDisplay = MAX_CHART_DISPLAY / 2;

    const minBrushItems = Math.max(
      maxDisplay,
      Math.ceil(dataframe.length * MIN_BRUSH_WIDTH),
    );
    const endIndex = Math.min(minBrushItems - 1, dataframe.length - 1);

    return { startIndex: 0, endIndex };
  };

  const brushRange = calculateBrushRange();

  const barProps = useMemo(
    () =>
      y.map((yItem, i) => ({
        yItem,
        fill: colorToHex(colors[i % colors.length]),
        radius:
          y.length - 1 === i
            ? ([4, 4, 0, 0] as [number, number, number, number])
            : 0,
      })),
    [y, colors],
  );

  const Chart = (
    <BarChart
      style={{
        scrollbarWidth: 'none',
      }}
      width={getChartFullWidth(dataframe.length)}
      height={
        isCompactView ? getChartFullWidth(dataframe.length) : barChartHeight
      }
      layout={isCompactView ? 'vertical' : 'horizontal'}
      data={dataframe}
      margin={
        isCompactView
          ? { top: 5, left: -60, bottom: 12 }
          : { top: 20, left: 40, bottom: 25 }
      }
    >
      <CartesianGrid
        strokeDasharray="3 3"
        stroke={RECHARTS_COLORS.BORDER_PRIMARY}
      />
      <XAxis {...xAxisProps} />
      <YAxis {...yAxisProps} />
      {shouldRenderBrush && !isExport && isCompactView && (
        <Brush
          dataKey={x}
          height={isCompactView ? 20 : 10}
          stroke="transparent"
          fill="transparent"
          travellerWidth={0}
          tickFormatter={() => ''}
          traveller={<rect />}
          startIndex={brushRange.startIndex}
          endIndex={brushRange.endIndex}
        >
          <rect className="recharts-brush-bg" />
        </Brush>
      )}
      <Tooltip
        payloadUniqBy={(payload) => payload.value}
        cursor={
          <rect
            fill={
              isCompactView ? 'transparent' : RECHARTS_COLORS.BORDER_SECONDARY
            }
            stroke={RECHARTS_COLORS.BORDER_SECONDARY}
          />
        }
        content={({ payload, active }) => renderTooltip(payload, !!active)}
      />
      {barProps.map(({ yItem, fill, radius }) => (
        <Bar
          key={yItem}
          dataKey={yItem}
          stackId={x}
          maxBarSize={16}
          fill={fill}
          radius={radius}
          isAnimationActive={false}
        />
      ))}
    </BarChart>
  );

  if (isCompactView) {
    return (
      <>
        <BarChartLegend y={y} colors={colors} />
        <ResponsiveContainer
          className="barchart-brush"
          width="99%"
          height={barChartHeight}
        >
          {Chart}
        </ResponsiveContainer>
      </>
    );
  }

  return (
    <div
      className="h-full"
      style={{
        width: getChartFullWidth(dataframe.length),
      }}
    >
      <BarChartLegend y={y} colors={colors} />
      {Chart}
    </div>
  );
};

export default CustomBarChart;
